import React, { useState } from 'react';
import Button from '../ui/Button';
import Modal, { ModalContent, ModalFooter } from '../ui/Modal';
import { giftsService } from '../../services/giftsService';

interface TierRef {
  id: string;
  name: string;
  amount: number;
  currency: string;
}

interface CreatorRef {
  displayName: string;
  id?: string;
}

interface GiftPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  tier: TierRef | null;
  creator: CreatorRef;
}

const GiftPurchaseModal: React.FC<GiftPurchaseModalProps> = ({ isOpen, onClose, tier, creator }) => {
  const [months, setMonths] = useState<number>(3);
  const [recipientName, setRecipientName] = useState<string>('');
  const [recipientEmail, setRecipientEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [deliverAt, setDeliverAt] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const reset = () => {
    setMonths(3);
    setRecipientName('');
    setRecipientEmail('');
    setMessage('');
    setDeliverAt('');
  };

  const handleSubmit = async () => {
    if (!tier) return;
    setIsSubmitting(true);
    try {
      const payload: any = {
        tierId: tier.id,
        months,
      };
      if (recipientEmail) payload.recipientEmail = recipientEmail;
      if (recipientName) payload.recipientName = recipientName;
      if (message) payload.message = message;
      if (deliverAt) payload.deliverAt = new Date(deliverAt).toISOString();

      const res = await giftsService.purchaseGift(payload);
      // Redirect to Stripe Checkout
      window.location.href = res.checkoutUrl;
    } catch (error: any) {
      console.error('Failed to start gift checkout:', error);
      alert(error?.response?.data?.message || 'Failed to start checkout. Please try again.');
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { onClose(); }} title={`Gift a membership to ${creator.displayName}`} size="md">
      <ModalContent>
        {!tier ? (
          <div className="text-center text-gray-600">No tier selected.</div>
        ) : (
          <div className="space-y-6">
            {/* Tier summary */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">{tier.name}</h3>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(tier.amount, tier.currency)}</p>
              <p className="text-gray-600">per month</p>
            </div>

            {/* Months */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gift duration (months)</label>
              <input
                type="number"
                min={1}
                max={36}
                value={months}
                onChange={(e) => setMonths(Math.min(36, Math.max(1, Number(e.target.value) || 1)))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-sm text-gray-500 mt-1">You can gift between 1 and 36 months.</p>
            </div>

            {/* Recipient details (optional) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recipient name (optional)</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="e.g. Alex Johnson"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recipient email (optional)</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="e.g. alex@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message (optional)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a personal note"
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[80px]"
              />
              <p className="text-sm text-gray-500 mt-1">Up to 500 characters.</p>
            </div>

            {/* Delivery date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Deliver on (optional)</label>
              <input
                type="date"
                value={deliverAt}
                onChange={(e) => setDeliverAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-sm text-gray-500 mt-1">Choose a future date to deliver the gift email.</p>
            </div>
          </div>
        )}
      </ModalContent>

      <ModalFooter>
        <Button
          variant="outline"
          onClick={() => { reset(); onClose(); }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!tier}
        >
          Continue to Checkout
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GiftPurchaseModal;
